////////////////////////////////////////
// # GOLDEN RULE!!!! HONOR ABOVE ALL!!!!
////////////////////////////////////////
// NO NEW FILES!!!!!!!!!
////////////////////////////////////////

// I. FILE HEADER [WHITE #FFFFFF]
// ⚪ Socratic AI Interviewer Pod for Taytrom Publishing Co - Container component with logic and state

// III. IMPORTS [GREEN #00FF00]
// 🟢 Third-party dependencies
import { useState, useEffect, useRef, useMemo, useCallback, FC } from 'react';
import toast from 'react-hot-toast';

// 🟢 First-party components
import SocraticAiInterviewerPea from '../pea/TaytromPublishingCo_Kings_Home_SRC_SocraticAIInterviewer_Lobby_Pea.js';
import { taytromPubHomeFrontendLogger } from '../../../logger/TaytromPublishingCo_Kings_Home_SRC_Logger.js';
import { STR_FSub_D_G_DVT_R_IRO_BACKEND_API_URL } from '../../../types/TaytromPublishingCo_Kings_Home_SRC_Types.js';

// IV. INDEPENDENT CONSTANTS [TEAL #00FFFF]
// 🔵 Component configuration
interface PodSocraticAiInterviewerProps {
  className?: string;
}

// 🔵 Chat message interface
interface ChatMessage {
  messageId: string;
  role: 'user' | 'assistant' | 'system';
  content: string;
  timestamp: Date;
  phase: number;
  followUpSuggestions?: string[];
  isEdited?: boolean;
  editedAt?: Date;
}

// 🔵 Interview phase configuration
interface InterviewPhase {
  phaseNumber: number;
  title: string;
  icon: string;
}

// 🔵 API Response types (Type Safety)
interface ConversationResponse {
  data: {
    conversationId: string;
    currentPhase: number;
    createdAt: string;
    updatedAt: string;
  };
}

// UNUSED: Keeping for future reference
// interface MessageResponse {
//   data: {
//     messageId: string;
//     role: 'user' | 'assistant' | 'system';
//     content: string;
//     timestamp: string;
//     phase: number;
//     followUpSuggestions?: string[];
//   };
// }

interface MessagesListResponse {
  data: Array<{
    messageId: string;
    role: 'user' | 'assistant' | 'system';
    content: string;
    timestamp: string;
    phase: number;
    metadata?: {
      followUpSuggestions?: string[];
    };
  }>;
}

interface StreamingMessageResponse {
  data: {
    aiMessage: {
      messageId: string;
      content: string;
      timestamp: string;
      phase: number;
      followUpSuggestions?: string[];
    };
  };
}

// 🔵 SSE Event types
// UNUSED: Keeping for future reference
// interface SSETokenEvent {
//   type: 'token';
//   data: string;
// }

// interface SSECompleteEvent {
//   type: 'complete';
//   data: {
//     aiMessage: {
//       messageId: string;
//       content: string;
//       timestamp: string;
//       followUpSuggestions?: string[];
//     };
//   };
// }

// interface SSEErrorEvent {
//   type: 'error';
//   data: {
//     error: string;
//   };
// }

// type SSEEvent = SSETokenEvent | SSECompleteEvent | SSEErrorEvent;

// 🔵 Validation result interface
interface ValidationResult {
  isValid: boolean;
  error?: string;
}

// 🔵 Rate limit tracking
// UNUSED: Keeping for future reference
// interface RateLimitState {
//   timestamps: number[];
//   maxRequests: number;
//   windowMs: number;
// }

const INTERVIEW_PHASES: InterviewPhase[] = [
  { phaseNumber: 1, title: 'Origin Story & Problem', icon: '🌱' },
  { phaseNumber: 2, title: 'Product & Technology', icon: '⚙️' },
  { phaseNumber: 3, title: 'Market & Competition', icon: '📊' },
  { phaseNumber: 4, title: 'Vision & Strategy', icon: '🎯' },
  { phaseNumber: 5, title: 'Philosophy & Leadership', icon: '👤' }
];

// 🔵 Rate limiting configuration
const RATE_LIMIT_MAX_REQUESTS = 5;
const RATE_LIMIT_WINDOW_MS = 60000; // 1 minute
const MAX_MESSAGE_LENGTH = 5000;

// V. FUNCTION/CLASS NAME [BLUE #0000FF] - OUTPUT DECLARATION
// 🔵 Socratic AI Interviewer Pod Component
const Pod_SocraticAiInterviewer: FC<PodSocraticAiInterviewerProps> = ({ className }) => {
  // VI. DECLARATIONS [PURPLE #FF00FF] - REQUIREMENTS SETUP
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [inputValue, setInputValue] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isAiTyping, setIsAiTyping] = useState<boolean>(false);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [currentPhase, setCurrentPhase] = useState<number>(1);
  const [error, setError] = useState<string | null>(null);
  const [conversationStartTime, _setConversationStartTime] = useState<Date>(new Date());
  const [timeInvested, setTimeInvested] = useState<number>(0); // seconds
  const [currentFollowUps, setCurrentFollowUps] = useState<string[]>([]); // 🔥 Follow-up suggestions
  const [editingMessageId, setEditingMessageId] = useState<string | null>(null); // Track which message is being edited
  const [editingContent, setEditingContent] = useState<string>(''); // Content of message being edited
  const [messageSendTimestamps, setMessageSendTimestamps] = useState<number[]>([]); // 🔥 Rate limiting
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null); // Reference to message input for keyboard shortcuts

  // ═══════════════════════════════════════════════════════════════
  // UTILITY FUNCTIONS (Inline - No New Files)
  // ═══════════════════════════════════════════════════════════════

  /**
   * V_G_DVT_R_IRO_validateMessageInput
   * Validates user message input for length and content
   *
   * @param message - User input message
   * @returns ValidationResult with isValid flag and optional error message
   */
  const V_G_DVT_R_IRO_validateMessageInput = useCallback((message: string): ValidationResult => {
    if (!message.trim()) {
      return { isValid: false, error: 'Message cannot be empty' };
    }

    if (message.length > MAX_MESSAGE_LENGTH) {
      return {
        isValid: false,
        error: `Message too long (max ${MAX_MESSAGE_LENGTH} characters)`
      };
    }

    // XSS prevention: check for script tags
    if (/<script[^>]*>.*<\/script>/gi.test(message)) {
      return {
        isValid: false,
        error: 'Invalid message content'
      };
    }

    return { isValid: true };
  }, []);

  /**
   * V_G_DVT_R_IRO_checkRateLimit
   * Checks if user has exceeded rate limit for sending messages
   *
   * @returns ValidationResult with isValid flag and optional error message
   */
  const V_G_DVT_R_IRO_checkRateLimit = useCallback((): ValidationResult => {
    const now = Date.now();
    const recentTimestamps = messageSendTimestamps.filter(
      timestamp => now - timestamp < RATE_LIMIT_WINDOW_MS
    );

    if (recentTimestamps.length >= RATE_LIMIT_MAX_REQUESTS) {
      const oldestTimestamp = recentTimestamps[0];
      const retryAfterSeconds = Math.ceil((oldestTimestamp + RATE_LIMIT_WINDOW_MS - now) / 1000);

      return {
        isValid: false,
        error: `Rate limit exceeded. Please wait ${retryAfterSeconds} seconds.`
      };
    }

    return { isValid: true };
  }, [messageSendTimestamps]);

  /**
   * T_G_DVT_R_IRO_formatTime
   * Formats seconds into human-readable "Xm Ys" format
   *
   * @param seconds - Total seconds to format
   * @returns Formatted time string (e.g., "5m 30s")
   */
  const T_G_DVT_R_IRO_formatTime = useCallback((seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}m ${secs}s`;
  }, []);

  // VII. INPUT DECLARATIONS [CYAN #00FFFF]
  // 🔵 TIRO: Initialize or restore conversation on mount
  useEffect(() => {
    const initializeOrRestoreConversation_VSubVSoVSt_T_L_DVT_R_IRO = async (): Promise<void> => {
      try {
        // T.1: Check localStorage for existing conversation
        const savedConversationId = localStorage.getItem('tpc_socratic_interview_conversationId');

        if (savedConversationId) {
          // ⚠️ SECURITY WARNING: localStorage usage for conversation ID
          // This is a KNOWN SECURITY ISSUE per CLAUDE.md analysis.
          // RECOMMENDED FIX: Replace with httpOnly cookies + backend session management
          // RISK: XSS vulnerability, data loss on clear, cross-tab sync issues
          // TODO: Migrate to cookie-based session storage (Phase 1 improvement)

          taytromPubHomeFrontendLogger.info('Attempting to restore conversation', {
            conversationId: savedConversationId
          });

          try {
            // T.2: Fetch conversation details from backend
            const convResponse = await fetch(
              `${STR_FSub_D_G_DVT_R_IRO_BACKEND_API_URL}/api/ai-chat/conversations/${savedConversationId}`
            );

            if (convResponse.ok) {
              const convData: ConversationResponse = await convResponse.json();

              // T.3: Fetch message history
              const messagesResponse = await fetch(
                `${STR_FSub_D_G_DVT_R_IRO_BACKEND_API_URL}/api/ai-chat/messages/${savedConversationId}`
              );

              if (messagesResponse.ok) {
                const messagesData: MessagesListResponse = await messagesResponse.json();

                // T.4: Restore state from backend data
                setConversationId(convData.data.conversationId);
                setCurrentPhase(convData.data.currentPhase || 1);

                // Convert timestamp strings back to Date objects (TYPED properly)
                const restoredMessages: ChatMessage[] = messagesData.data.map((msg) => ({
                  messageId: msg.messageId,
                  role: msg.role,
                  content: msg.content,
                  timestamp: new Date(msg.timestamp),
                  phase: msg.phase,
                  followUpSuggestions: msg.metadata?.followUpSuggestions || []
                }));

                setMessages(restoredMessages);

                // T.5: Restore last follow-up suggestions if available
                const reversedMessages = [...messagesData.data].reverse();
                const lastAiMessage = reversedMessages.find((m) => m.role === 'assistant');
                if (lastAiMessage?.metadata?.followUpSuggestions) {
                  setCurrentFollowUps(lastAiMessage.metadata.followUpSuggestions);
                }

                taytromPubHomeFrontendLogger.info('Conversation restored successfully', {
                  conversationId: savedConversationId,
                  messagesCount: restoredMessages.length,
                  phase: convData.data.currentPhase
                });

                // Toast notification: Conversation restored
                toast.success(`Interview conversation restored! ${restoredMessages.length} messages recovered.`);

                return; // Successfully restored - exit early
              }
            }

            // If restoration failed, clear localStorage and create new
            taytromPubHomeFrontendLogger.warn('Failed to restore conversation, creating new one', {
              conversationId: savedConversationId
            });
            localStorage.removeItem('tpc_socratic_interview_conversationId');
          } catch (restoreErr) {
            taytromPubHomeFrontendLogger.error('Error during conversation restoration', { error: restoreErr });
            localStorage.removeItem('tpc_socratic_interview_conversationId');
          }
        }

        // T.6: No saved conversation or restoration failed - create new
        taytromPubHomeFrontendLogger.info('Creating new AI interview conversation');

        const response = await fetch(`${STR_FSub_D_G_DVT_R_IRO_BACKEND_API_URL}/api/ai-chat/conversations`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            userEmail: undefined,
            userCompany: undefined,
            userId: undefined
          })
        });

        if (!response.ok) {
          throw new Error(`Failed to create conversation: ${response.status}`);
        }

        const data: ConversationResponse = await response.json();

        // T.7: Store conversation ID in state and localStorage
        // ⚠️ SECURITY WARNING: See note above about localStorage security issues
        setConversationId(data.data.conversationId);
        setCurrentPhase(data.data.currentPhase || 1);
        localStorage.setItem('tpc_socratic_interview_conversationId', data.data.conversationId);

        taytromPubHomeFrontendLogger.info('New conversation initialized', {
          conversationId: data.data.conversationId,
          phase: data.data.currentPhase
        });

        // T.8: Add welcome message for new conversations
        const welcomeMessage: ChatMessage = {
          messageId: 'welcome-' + Date.now(),
          role: 'assistant',
          content: `Hi, I'm **Cicero** 🎙️—an AI interviewer created by **Taytrom Publishing Co.** to conduct comprehensive Socratic interviews for writing definitive books about technical companies.\n\nTaytrom Publishing Co. specializes in comprehensive analysis books about B2B SaaS and infrastructure companies. I've been trained on the company's interviewing methodology, developed by founder Rob Taytrom (former corporate attorney turned full-stack engineer who built BitsBound).\n\nI'll be conducting your interview through 5 progressive phases:\n\n🌱 **Phase 1:** Origin Story & Problem Identification\n⚙️ **Phase 2:** Product & Technology Deep-Dive\n📊 **Phase 3:** Market Context & Competitive Landscape\n🎯 **Phase 4:** Vision & Strategic Projection\n👤 **Phase 5:** Personal Philosophy & Leadership\n\nLet's begin! Tell me about your company in 3-5 sentences. What problem do you solve and for whom?`,
          timestamp: new Date(),
          phase: 1
        };

        setMessages([welcomeMessage]);
      } catch (err) {
        taytromPubHomeFrontendLogger.error('Failed to initialize conversation', { error: err });
        const errorMessage = 'Failed to initialize chat. Please refresh the page.';
        setError(errorMessage);
        toast.error(errorMessage);
      }
    };

    initializeOrRestoreConversation_VSubVSoVSt_T_L_DVT_R_IRO();

    return () => {
      taytromPubHomeFrontendLogger.debug('Socratic AI Interviewer Pod unmounted');
    };
  }, []);

  // 🔵 Auto-scroll to latest message
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isAiTyping]);

  // 🔵 Time tracker: Update every second
  useEffect(() => {
    timerRef.current = setInterval(() => {
      const elapsed = Math.floor((Date.now() - conversationStartTime.getTime()) / 1000);
      setTimeInvested(elapsed);
    }, 1000);

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [conversationStartTime]);

  // 🔵 Keyboard shortcuts for power users
  useEffect(() => {
    // ═══════════════════════════════════════════════════════════════
    // TIR{DVT}O: KEYBOARD SHORTCUTS
    // ═══════════════════════════════════════════════════════════════
    // TRIGGER: User presses keyboard shortcut
    // INPUT: KeyboardEvent
    // REQUIREMENTS {DVT}:
    //   D: Shortcut key combinations (Ctrl/Cmd + K, Enter, etc.)
    //   V: Is target input? Is modifier key pressed?
    //   T: Execute corresponding action (focus, send, export, navigate)
    // OUTPUT: Action executed, toast notification (optional)
    // ═══════════════════════════════════════════════════════════════

    const VSubVSoVSt_T_L_DVT_R_IRO_handleKeyboardShortcut = (e: KeyboardEvent): void => {
      const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
      const modifierKey = isMac ? e.metaKey : e.ctrlKey;

      // Ctrl/Cmd + K: Focus message input
      if (modifierKey && e.key === 'k') {
        e.preventDefault();
        inputRef.current?.focus();
        return;
      }

      // Ctrl/Cmd + E: Export conversation
      if (modifierKey && e.key === 'e') {
        e.preventDefault();
        handleExportConversation_VSubVSoVSt_T_L_DVT_R_IRO();
        return;
      }

      // Ctrl/Cmd + /: Show keyboard shortcuts help
      if (modifierKey && e.key === '/') {
        e.preventDefault();
        const shortcutsList = isMac
          ? '⌘K: Focus input | ⌘E: Export | ⌘1-5: Navigate phases | ⌘/: Shortcuts | Esc: Clear/dismiss'
          : 'Ctrl+K: Focus input | Ctrl+E: Export | Ctrl+1-5: Navigate phases | Ctrl+/: Shortcuts | Esc: Clear/dismiss';
        toast(shortcutsList, { duration: 6000, icon: '⌨️' });
        return;
      }

      // Ctrl/Cmd + 1-5: Navigate to phases
      if (modifierKey && ['1', '2', '3', '4', '5'].includes(e.key)) {
        e.preventDefault();
        const phaseNumber = parseInt(e.key, 10);
        handlePhaseClick_VSubVSoVSt_T_L_DVT_R_IRO(phaseNumber);
        return;
      }

      // Escape: Clear input or dismiss error
      if (e.key === 'Escape') {
        if (error) {
          // Dismiss error first if present
          handleDismissError_VSubVSoVSt_T_L_DVT_R_IRO();
        } else if (inputValue.trim()) {
          // Clear input if it has content
          setInputValue('');
          toast('Input cleared', { icon: '🗑️', duration: 2000 });
        }
        return;
      }
    };

    window.addEventListener('keydown', VSubVSoVSt_T_L_DVT_R_IRO_handleKeyboardShortcut);

    return () => {
      window.removeEventListener('keydown', VSubVSoVSt_T_L_DVT_R_IRO_handleKeyboardShortcut);
    };
  }, [error, inputValue]); // Dependencies: error and inputValue for Escape key behavior

  // VIII. OPERATIONS [ORANGE #FFA500] - REQUIREMENTS PROCESSING
  // 🔵 TIRO: Send user message and get streaming AI response
  const handleSendMessage_VSubVSoVSt_T_L_DVT_R_IRO = useCallback(async (): Promise<void> => {
    if (!inputValue.trim() || !conversationId || isLoading) {
      return;
    }

    // T.0: Validate message input
    const validation = V_G_DVT_R_IRO_validateMessageInput(inputValue);
    if (!validation.isValid) {
      setError(validation.error || 'Invalid message');
      toast.error(validation.error || 'Invalid message');
      return;
    }

    // T.0.1: Check rate limit
    const rateLimitCheck = V_G_DVT_R_IRO_checkRateLimit();
    if (!rateLimitCheck.isValid) {
      setError(rateLimitCheck.error || 'Rate limit exceeded');
      toast.error(rateLimitCheck.error || 'Rate limit exceeded', { icon: '⏱️', duration: 4000 });
      return;
    }

    // T.0.2: Add timestamp for rate limiting
    setMessageSendTimestamps(prev => [...prev, Date.now()]);

    const userMessageContent = inputValue.trim();
    const userMessageId = 'user-' + Date.now();
    setInputValue('');
    setIsLoading(true);
    setError(null);

    // T.1: Add user message to UI immediately
    const userMessage: ChatMessage = {
      messageId: userMessageId,
      role: 'user',
      content: userMessageContent,
      timestamp: new Date(),
      phase: currentPhase
    };

    setMessages(prev => [...prev, userMessage]);
    setIsAiTyping(true);

    try {
      taytromPubHomeFrontendLogger.info('Sending message to AI with streaming', {
        conversationId,
        phase: currentPhase,
        messageLength: userMessageContent.length
      });

      // T.2: Create streaming request with SSE
      const response = await fetch(`${STR_FSub_D_G_DVT_R_IRO_BACKEND_API_URL}/api/ai-chat/messages/stream`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          conversationId,
          content: userMessageContent,
          phase: currentPhase
        })
      });

      if (!response.ok) {
        throw new Error(`Failed to send message: ${response.status}`);
      }

      // T.3: Parse SSE stream
      const reader = response.body?.getReader();
      const decoder = new TextDecoder();

      if (!reader) {
        throw new Error('No response body reader available');
      }

      const streamingMessageId = 'ai-streaming-' + Date.now();
      let accumulatedContent = '';
      let finalAiMessage: StreamingMessageResponse['data']['aiMessage'] | null = null;

      // Create placeholder AI message for streaming
      const placeholderMessage: ChatMessage = {
        messageId: streamingMessageId,
        role: 'assistant',
        content: '',
        timestamp: new Date(),
        phase: currentPhase
      };

      setMessages(prev => [...prev, placeholderMessage]);

      // T.4: Read stream chunks
      let buffer = '';
      while (true) {
        const { done, value } = await reader.read();

        if (done) {
          break;
        }

        // Decode and parse SSE events
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');

        // Process complete lines, keep incomplete line in buffer
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const eventData = JSON.parse(line.substring(6));

              if (eventData.type === 'token') {
                // Accumulate token and update message in real-time
                accumulatedContent += eventData.data;

                setMessages(prev =>
                  prev.map(msg =>
                    msg.messageId === streamingMessageId
                      ? { ...msg, content: accumulatedContent }
                      : msg
                  )
                );
              } else if (eventData.type === 'complete') {
                // Stream complete - save final message data
                finalAiMessage = eventData.data.aiMessage;

                // V: Null safety check for finalAiMessage
                if (finalAiMessage) {
                  // D: Capture non-null value for use in callbacks (TypeScript type narrowing)
                  const message = finalAiMessage;

                  // Update with final message ID and metadata
                  setMessages(prev =>
                    prev.map(msg =>
                      msg.messageId === streamingMessageId
                        ? {
                            ...msg,
                            messageId: message.messageId,
                            content: message.content,
                            timestamp: new Date(message.timestamp)
                          }
                        : msg
                    )
                  );

                  // T.5: Update follow-up suggestions
                  if (message.followUpSuggestions && Array.isArray(message.followUpSuggestions)) {
                    setCurrentFollowUps(message.followUpSuggestions);
                    taytromPubHomeFrontendLogger.debug('Follow-up suggestions received', {
                      count: message.followUpSuggestions.length,
                      suggestions: message.followUpSuggestions
                    });
                  }

                  taytromPubHomeFrontendLogger.info('Streaming AI response completed', {
                    messageId: message.messageId,
                    responseLength: message.content.length,
                    followUpsReceived: message.followUpSuggestions?.length || 0
                  });
                }
              } else if (eventData.type === 'error') {
                throw new Error(eventData.data.error || 'Streaming failed');
              }
            } catch (parseErr) {
              taytromPubHomeFrontendLogger.warn('Failed to parse SSE event', { line, error: parseErr });
            }
          }
        }
      }
    } catch (err) {
      taytromPubHomeFrontendLogger.error('Failed to send message with streaming', { error: err });
      const errorMessage = 'Failed to send message. Please try again.';
      setError(errorMessage);
      toast.error(errorMessage);

      // Remove the user message if sending failed
      setMessages(prev => prev.filter(msg => msg.messageId !== userMessageId));
    } finally {
      setIsLoading(false);
      setIsAiTyping(false);
    }
  }, [inputValue, conversationId, isLoading, currentPhase, V_G_DVT_R_IRO_validateMessageInput, V_G_DVT_R_IRO_checkRateLimit]);

  // 🔵 Handle input change
  const handleInputChange_VSubVSoVSt_T_L_DVT_R_IRO = useCallback((value: string): void => {
    setInputValue(value);
  }, []);

  // 🔵 Handle Enter key press
  const handleKeyPress_VSubVSoVSt_T_L_DVT_R_IRO = useCallback((e: React.KeyboardEvent<HTMLTextAreaElement>): void => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage_VSubVSoVSt_T_L_DVT_R_IRO();
    }
  }, [handleSendMessage_VSubVSoVSt_T_L_DVT_R_IRO]);

  // 🔵 Handle retry last failed message
  const handleRetryLastMessage_VSubVSoVSt_T_L_DVT_R_IRO = async (): Promise<void> => {
    // T.1: Find the last user message (which failed to get AI response)
    const userMessages = messages.filter(m => m.role === 'user');
    if (userMessages.length === 0 || !conversationId) {
      return;
    }

    const lastUserMessage = userMessages[userMessages.length - 1];

    taytromPubHomeFrontendLogger.info('Retrying last message', {
      messageId: lastUserMessage.messageId,
      contentLength: lastUserMessage.content.length
    });

    // T.2: Clear error and set loading states
    setError(null);
    setIsLoading(true);
    setIsAiTyping(true);

    try {
      // T.3: Retry the API call with the last user message content
      const response = await fetch(`${STR_FSub_D_G_DVT_R_IRO_BACKEND_API_URL}/api/ai-chat/messages`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          conversationId,
          content: lastUserMessage.content,
          phase: currentPhase
        })
      });

      if (!response.ok) {
        throw new Error(`Failed to send message: ${response.status}`);
      }

      const data = await response.json();

      // T.4: Add AI response to messages
      const aiMessage: ChatMessage = {
        messageId: data.data.aiMessage.messageId,
        role: 'assistant',
        content: data.data.aiMessage.content,
        timestamp: new Date(data.data.aiMessage.timestamp),
        phase: data.data.aiMessage.phase
      };

      setMessages(prev => [...prev, aiMessage]);

      // T.5: Update follow-up suggestions from AI response
      if (data.data.aiMessage.followUpSuggestions && Array.isArray(data.data.aiMessage.followUpSuggestions)) {
        setCurrentFollowUps(data.data.aiMessage.followUpSuggestions);
      }

      taytromPubHomeFrontendLogger.info('Retry successful', {
        messageId: aiMessage.messageId,
        responseLength: aiMessage.content.length
      });

      // Toast notification: Retry successful
      toast.success('Message sent successfully! AI response received.');
    } catch (err) {
      taytromPubHomeFrontendLogger.error('Retry failed', { error: err });
      const errorMessage = 'Failed to send message. Please try again.';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setIsLoading(false);
      setIsAiTyping(false);
    }
  };

  // 🔵 Handle dismiss error message
  const handleDismissError_VSubVSoVSt_T_L_DVT_R_IRO = (): void => {
    setError(null);
    taytromPubHomeFrontendLogger.debug('Error dismissed');
  };

  // 🔵 Handle phase navigation - scroll to first message of that phase
  const handlePhaseClick_VSubVSoVSt_T_L_DVT_R_IRO = (phaseNumber: number): void => {
    try {
      taytromPubHomeFrontendLogger.info('Navigating to phase', { phaseNumber });

      // T.1: Find first message from selected phase
      const phaseMessages = messages.filter(m => m.phase === phaseNumber);

      if (phaseMessages.length > 0) {
        // T.2: Find the DOM element for the first message of this phase
        const firstPhaseMessageId = phaseMessages[0].messageId;
        const messageElement = document.querySelector(`[data-message-id="${firstPhaseMessageId}"]`);

        if (messageElement) {
          // T.3: Smooth scroll to the message
          messageElement.scrollIntoView({ behavior: 'smooth', block: 'center' });

          taytromPubHomeFrontendLogger.debug('Scrolled to phase', {
            phaseNumber,
            messageId: firstPhaseMessageId
          });
        } else {
          taytromPubHomeFrontendLogger.warn('Could not find message element for phase', {
            phaseNumber,
            messageId: firstPhaseMessageId
          });
        }
      } else {
        taytromPubHomeFrontendLogger.info('No messages found for phase', { phaseNumber });
      }
    } catch (err) {
      taytromPubHomeFrontendLogger.error('Failed to navigate to phase', { error: err, phaseNumber });
    }
  };

  // 🔵 Handle export conversation as JSON
  const handleExportConversation_VSubVSoVSt_T_L_DVT_R_IRO = (): void => {
    try {
      taytromPubHomeFrontendLogger.info('Exporting conversation', {
        conversationId,
        messagesCount: messages.length,
        phase: currentPhase
      });

      // T.1: Prepare export data structure
      const exportData = {
        conversationId,
        currentPhase,
        questionsAnswered: messages.filter(m => m.role === 'user').length,
        timeInvested,
        exportedAt: new Date().toISOString(),
        metadata: {
          totalMessages: messages.length,
          phases: INTERVIEW_PHASES,
          exportVersion: '1.0.0'
        },
        messages: messages.map(msg => ({
          messageId: msg.messageId,
          role: msg.role,
          content: msg.content,
          timestamp: msg.timestamp.toISOString(),
          phase: msg.phase
        }))
      };

      // T.2: Create JSON blob and download
      const jsonString = JSON.stringify(exportData, null, 2);
      const blob = new Blob([jsonString], { type: 'application/json' });
      const url = URL.createObjectURL(blob);

      // T.3: Create temporary download link and trigger
      const link = document.createElement('a');
      link.href = url;
      link.download = `tpc-interview-${conversationId}-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(link);
      link.click();

      // T.4: Cleanup
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      taytromPubHomeFrontendLogger.info('Conversation exported successfully', {
        conversationId,
        fileName: link.download
      });

      // Toast notification: Export successful
      toast.success(`Interview conversation exported successfully! ${messages.length} messages saved.`);
    } catch (err) {
      taytromPubHomeFrontendLogger.error('Failed to export conversation', { error: err });
      const errorMessage = 'Failed to export conversation. Please try again.';
      setError(errorMessage);
      toast.error(errorMessage);
    }
  };

  // 🔵 Handle start editing message
  const handleStartEditMessage_VSubVSoVSt_T_L_DVT_R_IRO = (messageId: string, currentContent: string): void => {
    taytromPubHomeFrontendLogger.info('Starting message edit', { messageId });
    setEditingMessageId(messageId);
    setEditingContent(currentContent);
  };

  // 🔵 Handle cancel editing message
  const handleCancelEditMessage_VSubVSoVSt_T_L_DVT_R_IRO = (): void => {
    taytromPubHomeFrontendLogger.debug('Cancelled message edit', { messageId: editingMessageId });
    setEditingMessageId(null);
    setEditingContent('');
  };

  // 🔵 Handle save edited message
  const handleSaveEditMessage_VSubVSoVSt_T_L_DVT_R_IRO = (): void => {
    if (!editingMessageId || !editingContent.trim()) {
      return;
    }

    taytromPubHomeFrontendLogger.info('Saving edited message', {
      messageId: editingMessageId,
      newContent: editingContent
    });

    // T.1: Update message in local state
    setMessages(prev =>
      prev.map(msg =>
        msg.messageId === editingMessageId
          ? { ...msg, content: editingContent.trim() }
          : msg
      )
    );

    // T.2: Clear editing state
    setEditingMessageId(null);
    setEditingContent('');

    // Toast notification
    toast.success('Message updated successfully!');

    // Note: In a production app, you would also update the backend here
    // Example:
    // await fetch(`${BACKEND_URL}/api/ai-chat/messages/${editingMessageId}`, {
    //   method: 'PATCH',
    //   body: JSON.stringify({ content: editingContent })
    // });
  };

  // 🔵 Handle delete message
  const handleDeleteMessage_VSubVSoVSt_T_L_DVT_R_IRO = (messageId: string): void => {
    taytromPubHomeFrontendLogger.info('Deleting message', { messageId });

    // T.1: Find the message being deleted
    const messageToDelete = messages.find(m => m.messageId === messageId);
    if (!messageToDelete) {
      return;
    }

    // T.2: Remove message from local state
    setMessages(prev => prev.filter(msg => msg.messageId !== messageId));

    // Toast notification
    toast.success('Message deleted', { icon: '🗑️', duration: 3000 });

    taytromPubHomeFrontendLogger.info('Message deleted successfully', { messageId });

    // Note: In a production app, you would also delete from backend here
    // Example:
    // await fetch(`${BACKEND_URL}/api/ai-chat/messages/${messageId}`, {
    //   method: 'DELETE'
    // });
  };

  // 🔵 Handle follow-up suggestion click - immediately send to AI
  const handleFollowUpClick_VSubVSoVSt_T_L_DVT_R_IRO = async (followUp: string): Promise<void> => {
    if (!conversationId || isLoading || !followUp.trim()) {
      return;
    }

    // Send follow-up suggestion directly as user's response
    // Follow-ups are already phrased as statements/prompts (e.g., "Tell me about your first customer")
    // So AI understands this IS the topic the user wants to explore
    const userMessageContent = followUp.trim();
    setCurrentFollowUps([]); // Clear suggestions immediately
    setIsLoading(true);
    setError(null);

    // T.1: Add user message to UI immediately
    const userMessage: ChatMessage = {
      messageId: 'user-' + Date.now(),
      role: 'user',
      content: userMessageContent,
      timestamp: new Date(),
      phase: currentPhase
    };

    setMessages(prev => [...prev, userMessage]);
    setIsAiTyping(true);

    try {
      taytromPubHomeFrontendLogger.info('Sending follow-up selection to AI', {
        conversationId,
        phase: currentPhase,
        followUp: userMessageContent
      });

      // T.2: Call backend API to send message and get AI response
      const response = await fetch(`${STR_FSub_D_G_DVT_R_IRO_BACKEND_API_URL}/api/ai-chat/messages`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          conversationId,
          content: userMessageContent,
          phase: currentPhase
        })
      });

      if (!response.ok) {
        throw new Error(`Failed to send message: ${response.status}`);
      }

      const data = await response.json();

      // T.3: Add AI response to messages
      const aiMessage: ChatMessage = {
        messageId: data.data.aiMessage.messageId,
        role: 'assistant',
        content: data.data.aiMessage.content,
        timestamp: new Date(data.data.aiMessage.timestamp),
        phase: data.data.aiMessage.phase
      };

      setMessages(prev => [...prev, aiMessage]);

      // T.4: Update follow-up suggestions from AI response
      if (data.data.aiMessage.followUpSuggestions && Array.isArray(data.data.aiMessage.followUpSuggestions)) {
        setCurrentFollowUps(data.data.aiMessage.followUpSuggestions);
      }

      taytromPubHomeFrontendLogger.info('AI response received for follow-up', {
        messageId: aiMessage.messageId,
        responseLength: aiMessage.content.length
      });
    } catch (err) {
      taytromPubHomeFrontendLogger.error('Failed to send follow-up', { error: err });
      const errorMessage = 'Failed to send message. Please try again.';
      setError(errorMessage);
      toast.error(errorMessage);

      // Remove the user message if sending failed
      setMessages(prev => prev.filter(msg => msg.messageId !== userMessage.messageId));
    } finally {
      setIsLoading(false);
      setIsAiTyping(false);
    }
  };

  // IX. PRE-RENDER [RED #FF0000] - REQUIREMENTS ASSEMBLY
  // Calculate progress metrics (Memoized for performance)
  const questionsAnswered = useMemo(
    () => messages.filter(m => m.role === 'user').length,
    [messages]
  );

  // Memoize formatted time string
  const formattedTimeInvested = useMemo(
    () => T_G_DVT_R_IRO_formatTime(timeInvested),
    [timeInvested, T_G_DVT_R_IRO_formatTime]
  );

  // Memoize podState object to prevent unnecessary Pea re-renders
  const podState = useMemo(() => ({
    messages,
    inputValue,
    isLoading,
    isAiTyping,
    currentPhase,
    phases: INTERVIEW_PHASES,
    error,
    messagesEndRef,
    inputRef,  // For keyboard shortcuts (Ctrl/Cmd + K to focus)
    questionsAnswered,
    timeInvested: formattedTimeInvested,
    followUpSuggestions: currentFollowUps,
    editingMessageId,
    editingContent,
    onSendMessage: handleSendMessage_VSubVSoVSt_T_L_DVT_R_IRO,
    onInputChange: handleInputChange_VSubVSoVSt_T_L_DVT_R_IRO,
    onKeyPress: handleKeyPress_VSubVSoVSt_T_L_DVT_R_IRO,
    onFollowUpClick: handleFollowUpClick_VSubVSoVSt_T_L_DVT_R_IRO,
    onExportClick: handleExportConversation_VSubVSoVSt_T_L_DVT_R_IRO,
    onPhaseClick: handlePhaseClick_VSubVSoVSt_T_L_DVT_R_IRO,
    onRetryMessage: handleRetryLastMessage_VSubVSoVSt_T_L_DVT_R_IRO,
    onDismissError: handleDismissError_VSubVSoVSt_T_L_DVT_R_IRO,
    onStartEditMessage: handleStartEditMessage_VSubVSoVSt_T_L_DVT_R_IRO,
    onCancelEditMessage: handleCancelEditMessage_VSubVSoVSt_T_L_DVT_R_IRO,
    onSaveEditMessage: handleSaveEditMessage_VSubVSoVSt_T_L_DVT_R_IRO,
    onEditingContentChange: setEditingContent,
    onDeleteMessage: handleDeleteMessage_VSubVSoVSt_T_L_DVT_R_IRO
  }), [
    messages,
    inputValue,
    isLoading,
    isAiTyping,
    currentPhase,
    error,
    questionsAnswered,
    formattedTimeInvested,
    currentFollowUps,
    editingMessageId,
    editingContent,
    handleSendMessage_VSubVSoVSt_T_L_DVT_R_IRO,
    handleInputChange_VSubVSoVSt_T_L_DVT_R_IRO,
    handleKeyPress_VSubVSoVSt_T_L_DVT_R_IRO,
    handleFollowUpClick_VSubVSoVSt_T_L_DVT_R_IRO,
    handleExportConversation_VSubVSoVSt_T_L_DVT_R_IRO,
    handlePhaseClick_VSubVSoVSt_T_L_DVT_R_IRO,
    handleRetryLastMessage_VSubVSoVSt_T_L_DVT_R_IRO,
    handleDismissError_VSubVSoVSt_T_L_DVT_R_IRO,
    handleStartEditMessage_VSubVSoVSt_T_L_DVT_R_IRO,
    handleCancelEditMessage_VSubVSoVSt_T_L_DVT_R_IRO,
    handleSaveEditMessage_VSubVSoVSt_T_L_DVT_R_IRO,
    handleDeleteMessage_VSubVSoVSt_T_L_DVT_R_IRO
  ]);

  // X. RENDER/EXPORTS [BLACK #000000] - OUTPUT DELIVERY
  return <SocraticAiInterviewerPea {...podState} className={className} />;
};

export default Pod_SocraticAiInterviewer;
